import { motion } from "framer-motion";
import {
  Microscope,
  Sparkles,
  Clock,
  Globe,
  ScrollText,
  Archive,
} from "lucide-react";
import AbstractShapes from "@/components/landing/AbstractShapes";

const ITEMS = [
  {
    n: "01",
    icon: Microscope,
    title: "Rigorous Peer Review",
    text: "Double-blind review by subject-matter experts from leading academic institutions, structured for methodological depth and clinical relevance.",
  },
  {
    n: "02",
    icon: Sparkles,
    title: "Open Access Visibility",
    text: "Every accepted manuscript is published under a CC-BY license — fully indexable, citable and discoverable across academic search engines.",
  },
  {
    n: "03",
    icon: Clock,
    title: "Rapid Editorial Decisions",
    text: "Editorial triage within 24–48 hours. Median first decision in 14 days. Online publication within 48 hours of acceptance.",
  },
  {
    n: "04",
    icon: Globe,
    title: "Global Reach",
    text: "Authors and reviewers from 40+ countries. Articles reach clinicians, researchers and institutions across the international research community.",
  },
  {
    n: "05",
    icon: ScrollText,
    title: "Publication Ethics",
    text: "Strict adherence to COPE guidelines, ICMJE recommendations, and the Declaration of Helsinki. iThenticate plagiarism screening on every submission.",
  },
  {
    n: "06",
    icon: Archive,
    title: "Long-Term Archiving",
    text: "Permanent DOI registration via Crossref. Articles are preserved in CLOCKSS and Portico for indefinite scholarly access.",
  },
];

export default function WhyPublish() {
  return (
    <section
      data-testid="why-publish-section"
      className="relative py-24 md:py-32 bg-[var(--sci-bg)] bg-grid-faint overflow-hidden"
    >
      <AbstractShapes
        variant="wave"
        tone="ink"
        className="right-0 top-0 w-[420px] h-[280px] opacity-50"
      />
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl mb-16 md:mb-20">
          <div className="overline text-[var(--sci-teal)] mb-5">
            · 03 / Why Publish With Us
          </div>
          <h2 className="font-serif-display text-4xl md:text-5xl lg:text-6xl text-[var(--sci-navy)] tracking-tight leading-[1.02]">
            Editorial standards <em className="not-italic text-[var(--sci-teal)]">researchers trust</em>.
          </h2>
          <p className="mt-6 text-[var(--sci-text-muted)] leading-relaxed text-base md:text-lg max-w-2xl">
            Six commitments that define what it means to publish with International
            Sci Ink Press — from review rigor to perpetual access.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-[var(--sci-border)] border border-[var(--sci-border)]">
          {ITEMS.map((it, i) => {
            const Icon = it.icon;
            return (
              <motion.div
                key={it.title}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.4 }}
                transition={{ duration: 0.55, delay: 0.05 * i }}
                data-testid={`why-card-${i}`}
                className="group bg-white p-8 md:p-10 transition-colors duration-300 hover:bg-[var(--sci-navy)] hover:text-white"
              >
                <div className="flex items-start justify-between mb-8">
                  <div className="font-mono-sci text-[11px] tracking-[0.25em] text-[var(--sci-text-muted)] group-hover:text-white/60">
                    {it.n} / 06
                  </div>
                  <div className="w-12 h-12 border border-[var(--sci-border)] group-hover:border-white/25 flex items-center justify-center text-[var(--sci-teal)] group-hover:text-white transition-colors">
                    <Icon size={20} strokeWidth={1.4} />
                  </div>
                </div>
                <h3 className="font-serif-display text-2xl md:text-[26px] leading-tight text-[var(--sci-navy)] group-hover:text-white transition-colors">
                  {it.title}
                </h3>
                <p className="mt-4 text-sm md:text-[15px] leading-relaxed text-[var(--sci-text-muted)] group-hover:text-white/75 transition-colors">
                  {it.text}
                </p>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
